import React, { PropsWithChildren } from "react";

import _ from "lodash";

export type SaveContextType = {
    onButtonSaveClick: () => Promise<unknown[]>;
};

export type SaveActionsContextType = {
    addSaveAction: (fnId: string, fn: () => Promise<unknown>) => void;
    removeSaveAction: (fnId: string) => void;
};

export const SaveContext = React.createContext({
    onButtonSaveClick: (): Promise<unknown[]> => {
        console.error("Undefined function -> SaveContext.onButtonSaveClick");

        return Promise.reject([]);
    },
});

export const SaveActionsContext = React.createContext<SaveActionsContextType>({
    addSaveAction: (fnId: string, fn: () => Promise<unknown>) => {
        console.error("Undefined function -> SaveActionsContext.addSaveAction");
    },
    removeSaveAction: (fnId: string) => {
        console.error("Undefined function -> SaveActionsContext.removeSaveAction");
    },
});

export const useSaveContext = () => React.useContext(SaveContext);

export const useSaveActionsContext = () => React.useContext(SaveActionsContext);

export const SaveContextProvider = ({ children }: PropsWithChildren<{}>) => {
    const [contextData, setContextData] = React.useState<{
        promisesFn: {
            [x: string]: () => Promise<unknown>;
        };
    }>({
        promisesFn: {},
    });

    const addSaveAction = React.useCallback((fnId: string, fn: () => Promise<unknown>) => {
        setContextData((prevValue) => {
            const newValue = _.cloneDeep(prevValue);

            newValue.promisesFn[fnId] = fn;

            return newValue;
        });
    }, []);

    const removeSaveAction = React.useCallback((fnId: string) => {
        setContextData((prevValue) => {
            const newValue = _.cloneDeep(prevValue);

            delete newValue.promisesFn[fnId];

            return newValue;
        });
    }, []);

    const onButtonSaveClick = React.useCallback((): Promise<unknown[]> => {
        const promises: Array<Promise<unknown>> = [];

        _.forEach(contextData.promisesFn, (fn) => {
            promises.push(fn());
        });

        return Promise.all(promises);
    }, [contextData]);

    const saveValues: SaveContextType = React.useMemo(
        (): SaveContextType => ({
            onButtonSaveClick,
        }),
        [onButtonSaveClick]
    );

    const saveActionsValues: SaveActionsContextType = React.useMemo(
        (): SaveActionsContextType => ({
            addSaveAction,
            removeSaveAction,
        }),
        [addSaveAction, removeSaveAction]
    );

    return (
        <SaveContext.Provider value={saveValues}>
            <SaveActionsContext.Provider value={saveActionsValues}>
                {children}
            </SaveActionsContext.Provider>
        </SaveContext.Provider>
    );
};
